<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';

import Switch from 'next/switch/Switch.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SectionLayout from './SectionLayout.vue';
import DateField from 'dashboard/components-next/date-field/DateField.vue';

const { t } = useI18n();
const store = useStore();
const { accountId } = useAccount();
const getAccount = useMapGetter('accounts/getAccount');
const uiFlags = useMapGetter('accounts/getUIFlags');

const assignOnOffline = ref(false);
const assignOnlyOnlineAgents = ref(false);
const removeOfflineAssigneeOnIncomingMessage = ref(false);
const autoReassignDelayMinutes = ref('');
const offlineReassignDelayMinutes = ref('');
const maxConversationsPerAgent = ref('');
const boostRules = ref([]);
const isSubmitting = ref(false);

const showBoostForm = ref(false);
const newRule = ref({
  name: '',
  boost_amount: '',
  start_date: '',
  end_date: '',
});
const boostFormError = ref('');
const formInputClass =
  'h-9 rounded-lg border border-n-weak bg-n-alpha-2 px-3 text-sm text-n-slate-12 outline-none transition-colors focus:border-n-brand focus:ring-1 focus:ring-n-brand';
const approxSign = '≈';
const arrowSign = '→';

const currentAccount = computed(() => getAccount.value(accountId.value) || {});

const activeBoostRules = computed(() => {
  const today = new Date().toISOString().slice(0, 10);
  return boostRules.value.filter(
    r => r.start_date <= today && r.end_date >= today
  );
});

const effectiveLimit = computed(() => {
  if (!maxConversationsPerAgent.value) return null;
  const base = Number(maxConversationsPerAgent.value);
  const boost = activeBoostRules.value.reduce(
    (sum, r) => sum + Number(r.boost_amount),
    0
  );
  return base + boost;
});

const loadSettings = () => {
  const settings = currentAccount.value?.settings || {};
  assignOnOffline.value = Boolean(settings.assign_on_offline);
  assignOnlyOnlineAgents.value = Boolean(settings.assign_only_online_agents);
  removeOfflineAssigneeOnIncomingMessage.value = Boolean(
    settings.remove_offline_assignee_on_incoming_message
  );
  autoReassignDelayMinutes.value = settings.auto_reassign_delay_minutes ?? '';
  offlineReassignDelayMinutes.value =
    settings.offline_reassign_delay_minutes ?? '';
  maxConversationsPerAgent.value = settings.max_conversations_per_agent ?? '';
  boostRules.value = Array.isArray(settings.agent_boost_rules)
    ? [...settings.agent_boost_rules]
    : [];
};

const saveSettings = async () => {
  isSubmitting.value = true;
  try {
    const max =
      maxConversationsPerAgent.value === ''
        ? null
        : Number(maxConversationsPerAgent.value);
    await store.dispatch('accounts/update', {
      assign_on_offline: assignOnOffline.value,
      assign_only_online_agents: assignOnlyOnlineAgents.value,
      remove_offline_assignee_on_incoming_message:
        removeOfflineAssigneeOnIncomingMessage.value,
      auto_reassign_delay_minutes:
        autoReassignDelayMinutes.value === ''
          ? null
          : Number(autoReassignDelayMinutes.value),
      offline_reassign_delay_minutes:
        offlineReassignDelayMinutes.value === ''
          ? null
          : Number(offlineReassignDelayMinutes.value),
      max_conversations_per_agent: max,
      agent_boost_rules: boostRules.value,
    });
    useAlert(t('AGENT_CAPACITY.SAVE_SUCCESS'), { variant: 'success' });
  } catch {
    useAlert(t('AGENT_CAPACITY.SAVE_ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

const resetBoostForm = () => {
  newRule.value = { name: '', boost_amount: '', start_date: '', end_date: '' };
  boostFormError.value = '';
  showBoostForm.value = false;
};

const addBoostRule = () => {
  const { name, boost_amount, start_date, end_date } = newRule.value;
  if (!name.trim() || !boost_amount || !start_date || !end_date) {
    boostFormError.value = t('AGENT_CAPACITY.BOOST.FORM_ERROR');
    return;
  }
  if (start_date > end_date) {
    boostFormError.value = t('AGENT_CAPACITY.BOOST.DATE_ERROR');
    return;
  }
  boostRules.value.push({
    id: `boost-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    name: name.trim(),
    boost_amount: Number(boost_amount),
    start_date,
    end_date,
  });
  resetBoostForm();
};

const removeBoostRule = id => {
  boostRules.value = boostRules.value.filter(r => r.id !== id);
};

const formatDate = date => {
  if (!date) return '';
  const [y, m, d] = date.split('-');
  return `${d}/${m}/${y}`;
};

const isRuleActive = rule => {
  const today = new Date().toISOString().slice(0, 10);
  return rule.start_date <= today && rule.end_date >= today;
};

onMounted(loadSettings);
</script>

<template>
  <SectionLayout
    :title="t('AGENT_CAPACITY.TITLE')"
    :description="t('AGENT_CAPACITY.DESCRIPTION')"
    with-border
  >
    <div class="space-y-6">
      <!-- Offline distribution -->
      <div class="rounded-xl border border-n-weak bg-n-alpha-1 p-4">
        <div class="flex items-start justify-between gap-4">
          <div class="flex-1">
            <div class="flex items-center gap-2 mb-1">
              <span class="i-lucide-wifi-off size-4 text-n-slate-10" />
              <p class="text-sm font-semibold text-n-slate-12">
                {{ t('AGENT_CAPACITY.OFFLINE.LABEL') }}
              </p>
            </div>
            <p class="text-xs text-n-slate-10 leading-relaxed">
              {{ t('AGENT_CAPACITY.OFFLINE.DESCRIPTION') }}
            </p>
          </div>
          <Switch v-model="assignOnOffline" />
        </div>
      </div>

      <!-- Assign only to online agents -->
      <div class="rounded-xl border border-n-weak bg-n-alpha-1 p-4">
        <div class="flex items-start justify-between gap-4">
          <div class="flex-1">
            <div class="flex items-center gap-2 mb-1">
              <span class="i-lucide-circle-dot size-4 text-n-teal-9" />
              <p class="text-sm font-semibold text-n-slate-12">
                {{ t('AGENT_CAPACITY.ONLINE_ONLY.LABEL') }}
              </p>
            </div>
            <p class="text-xs text-n-slate-10 leading-relaxed">
              {{ t('AGENT_CAPACITY.ONLINE_ONLY.DESCRIPTION') }}
            </p>
          </div>
          <Switch v-model="assignOnlyOnlineAgents" />
        </div>
      </div>

      <!-- Auto-reassign delay -->
      <div class="rounded-xl border border-n-weak bg-n-alpha-1 p-4 space-y-3">
        <div class="flex items-center gap-2">
          <span class="i-lucide-clock-arrow-up size-4 text-n-slate-10" />
          <p class="text-sm font-semibold text-n-slate-12">
            {{ t('AGENT_CAPACITY.AUTO_REASSIGN.LABEL') }}
          </p>
        </div>
        <p class="text-xs text-n-slate-10 leading-relaxed">
          {{ t('AGENT_CAPACITY.AUTO_REASSIGN.DESCRIPTION') }}
        </p>
        <div class="flex items-center gap-3">
          <input
            v-model="autoReassignDelayMinutes"
            type="number"
            min="1"
            :placeholder="t('AGENT_CAPACITY.AUTO_REASSIGN.PLACEHOLDER')"
            class="w-32 h-9 rounded-lg border border-n-weak bg-n-alpha-2 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand transition-colors"
          />
          <span class="text-xs text-n-slate-10">{{
            t('AGENT_CAPACITY.AUTO_REASSIGN.UNIT')
          }}</span>
          <span
            v-if="autoReassignDelayMinutes"
            class="text-xs text-n-blue-10 font-medium"
          >
            {{ approxSign }}
            {{
              Number(autoReassignDelayMinutes) >= 60
                ? (Number(autoReassignDelayMinutes) / 60).toFixed(1) + 'h'
                : autoReassignDelayMinutes + ' min'
            }}
          </span>
        </div>
      </div>

      <!-- Remove offline assignee when a new message arrives -->
      <div class="rounded-xl border border-n-weak bg-n-alpha-1 p-4">
        <div class="flex items-start justify-between gap-4">
          <div class="flex-1">
            <div class="flex items-center gap-2 mb-1">
              <span
                class="i-lucide-message-circle-warning size-4 text-n-slate-10"
              />
              <p class="text-sm font-semibold text-n-slate-12">
                {{ t('AGENT_CAPACITY.REMOVE_OFFLINE_ON_MESSAGE.LABEL') }}
              </p>
            </div>
            <p class="text-xs text-n-slate-10 leading-relaxed">
              {{ t('AGENT_CAPACITY.REMOVE_OFFLINE_ON_MESSAGE.DESCRIPTION') }}
            </p>
          </div>
          <Switch v-model="removeOfflineAssigneeOnIncomingMessage" />
        </div>
      </div>

      <!-- Offline-reassign delay -->
      <div class="rounded-xl border border-n-weak bg-n-alpha-1 p-4 space-y-3">
        <div class="flex items-center gap-2">
          <span class="i-lucide-user-x size-4 text-n-slate-10" />
          <p class="text-sm font-semibold text-n-slate-12">
            {{ t('AGENT_CAPACITY.OFFLINE_REASSIGN.LABEL') }}
          </p>
        </div>
        <p class="text-xs text-n-slate-10 leading-relaxed">
          {{ t('AGENT_CAPACITY.OFFLINE_REASSIGN.DESCRIPTION') }}
        </p>
        <div class="flex items-center gap-3">
          <input
            v-model="offlineReassignDelayMinutes"
            type="number"
            min="1"
            :placeholder="t('AGENT_CAPACITY.OFFLINE_REASSIGN.PLACEHOLDER')"
            class="w-32 h-9 rounded-lg border border-n-weak bg-n-alpha-2 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand transition-colors"
          />
          <span class="text-xs text-n-slate-10">{{
            t('AGENT_CAPACITY.OFFLINE_REASSIGN.UNIT')
          }}</span>
          <span
            v-if="offlineReassignDelayMinutes"
            class="text-xs text-n-blue-10 font-medium"
          >
            {{ approxSign }}
            {{
              Number(offlineReassignDelayMinutes) >= 60
                ? (Number(offlineReassignDelayMinutes) / 60).toFixed(1) + 'h'
                : offlineReassignDelayMinutes + ' min'
            }}
          </span>
        </div>
      </div>

      <!-- Max conversations per agent -->
      <div class="rounded-xl border border-n-weak bg-n-alpha-1 p-4 space-y-3">
        <div class="flex items-center gap-2">
          <span class="i-lucide-message-square size-4 text-n-slate-10" />
          <p class="text-sm font-semibold text-n-slate-12">
            {{ t('AGENT_CAPACITY.MAX_CONVOS.LABEL') }}
          </p>
        </div>
        <p class="text-xs text-n-slate-10 leading-relaxed">
          {{ t('AGENT_CAPACITY.MAX_CONVOS.DESCRIPTION') }}
        </p>
        <div class="flex items-center gap-3">
          <input
            v-model="maxConversationsPerAgent"
            type="number"
            min="1"
            :placeholder="t('AGENT_CAPACITY.MAX_CONVOS.PLACEHOLDER')"
            class="w-32 h-9 rounded-lg border border-n-weak bg-n-alpha-2 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand transition-colors"
          />
          <span
            v-if="effectiveLimit !== null"
            class="text-xs text-n-teal-10 font-medium"
          >
            {{
              t('AGENT_CAPACITY.MAX_CONVOS.EFFECTIVE', {
                limit: effectiveLimit,
              })
            }}
          </span>
        </div>
      </div>

      <!-- Boost rules -->
      <div class="rounded-xl border border-n-weak bg-n-alpha-1 p-4 space-y-4">
        <div class="flex items-center justify-between gap-3">
          <div class="flex items-center gap-2">
            <span class="i-lucide-zap size-4 text-n-amber-9" />
            <p class="text-sm font-semibold text-n-slate-12">
              {{ t('AGENT_CAPACITY.BOOST.LABEL') }}
            </p>
          </div>
          <button
            v-if="!showBoostForm"
            type="button"
            class="inline-flex items-center gap-1.5 rounded-lg border border-n-weak bg-n-alpha-2 px-3 py-1.5 text-xs font-medium text-n-slate-12 transition-colors hover:border-n-blue-8 hover:text-n-blue-10"
            @click="showBoostForm = true"
          >
            <span class="i-lucide-plus size-3.5" />
            {{ t('AGENT_CAPACITY.BOOST.ADD') }}
          </button>
        </div>

        <p class="text-xs text-n-slate-10 leading-relaxed -mt-2">
          {{ t('AGENT_CAPACITY.BOOST.DESCRIPTION') }}
        </p>

        <!-- Add rule form -->
        <div
          v-if="showBoostForm"
          class="rounded-lg border border-n-blue-7 bg-n-blue-2/30 p-4 space-y-3"
        >
          <p class="text-sm font-semibold text-n-slate-12">
            {{ t('AGENT_CAPACITY.BOOST.NEW_RULE') }}
          </p>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label class="flex flex-col gap-1.5 text-xs text-n-slate-11">
              <span>{{ t('AGENT_CAPACITY.BOOST.NAME') }}</span>
              <input
                v-model="newRule.name"
                type="text"
                :placeholder="t('AGENT_CAPACITY.BOOST.NAME_PLACEHOLDER')"
                :class="formInputClass"
              />
            </label>
            <label class="flex flex-col gap-1.5 text-xs text-n-slate-11">
              <span>{{ t('AGENT_CAPACITY.BOOST.AMOUNT') }}</span>
              <input
                v-model="newRule.boost_amount"
                type="number"
                min="1"
                :placeholder="t('AGENT_CAPACITY.BOOST.AMOUNT_PLACEHOLDER')"
                :class="formInputClass"
              />
            </label>
            <div class="flex flex-col gap-1.5 text-xs text-n-slate-11">
              <span>{{ t('AGENT_CAPACITY.BOOST.START_DATE') }}</span>
              <DateField
                v-model="newRule.start_date"
                :max="newRule.end_date || ''"
              />
            </div>
            <div class="flex flex-col gap-1.5 text-xs text-n-slate-11">
              <span>{{ t('AGENT_CAPACITY.BOOST.END_DATE') }}</span>
              <DateField
                v-model="newRule.end_date"
                :min="newRule.start_date || ''"
              />
            </div>
          </div>
          <p v-if="boostFormError" class="text-xs text-n-ruby-10">
            {{ boostFormError }}
          </p>
          <div class="flex gap-2 pt-1">
            <button
              type="button"
              class="h-8 rounded-lg border border-n-weak px-3 text-xs text-n-slate-11 hover:border-n-slate-6 transition-colors"
              @click="resetBoostForm"
            >
              {{ t('AGENT_CAPACITY.BOOST.CANCEL') }}
            </button>
            <button
              type="button"
              class="h-8 rounded-lg bg-n-blue-9 px-4 text-xs font-medium text-white hover:bg-n-blue-10 transition-colors"
              @click="addBoostRule"
            >
              {{ t('AGENT_CAPACITY.BOOST.CONFIRM') }}
            </button>
          </div>
        </div>

        <!-- Boost rules list -->
        <div v-if="boostRules.length" class="space-y-2">
          <div
            v-for="rule in boostRules"
            :key="rule.id"
            class="flex items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors"
            :class="
              isRuleActive(rule)
                ? 'border-n-amber-7 bg-n-amber-2/40'
                : 'border-n-weak bg-n-alpha-2'
            "
          >
            <span
              class="i-lucide-zap size-4 flex-shrink-0"
              :class="isRuleActive(rule) ? 'text-n-amber-9' : 'text-n-slate-9'"
            />
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-n-slate-12 truncate">
                {{ rule.name }}
              </p>
              <p class="text-xs text-n-slate-10">
                {{ formatDate(rule.start_date) }} {{ arrowSign }}
                {{ formatDate(rule.end_date) }}
              </p>
            </div>
            <span
              class="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-bold"
              :class="
                isRuleActive(rule)
                  ? 'bg-n-amber-3 text-n-amber-10'
                  : 'bg-n-slate-3 text-n-slate-10'
              "
            >
              {{ `+${rule.boost_amount}` }}
            </span>
            <span
              v-if="isRuleActive(rule)"
              class="hidden sm:inline text-[10px] font-medium uppercase text-n-amber-9 bg-n-amber-2 border border-n-amber-7 rounded px-1.5 py-0.5"
            >
              {{ t('AGENT_CAPACITY.BOOST.ACTIVE') }}
            </span>
            <button
              type="button"
              class="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded border border-transparent text-n-slate-9 transition-colors hover:border-n-ruby-7 hover:text-n-ruby-10"
              @click="removeBoostRule(rule.id)"
            >
              <span class="i-lucide-trash-2 size-3.5" />
            </button>
          </div>
        </div>
        <p v-else-if="!showBoostForm" class="text-xs text-n-slate-10 italic">
          {{ t('AGENT_CAPACITY.BOOST.EMPTY') }}
        </p>
      </div>

      <div class="flex justify-end">
        <NextButton
          blue
          :is-loading="isSubmitting || uiFlags.isUpdating"
          :disabled="isSubmitting || uiFlags.isUpdating"
          @click="saveSettings"
        >
          {{ t('AGENT_CAPACITY.SAVE') }}
        </NextButton>
      </div>
    </div>
  </SectionLayout>
</template>
